import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/auth';

export default function ProfilePage() {
  const { user } = useAuth();
  const [isCopied, setIsCopied] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error' | 'cooldown'>('idle');
  const { t, i18n } = useTranslation();

  if (!user) {
    return null;
  }

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(user.id);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // Ignore copy errors
    }
  };

  const handleResendVerification = async () => {
    setResendStatus('sending');
    try {
      await authAPI.resendVerification(user.email);
      setResendStatus('sent');
    } catch (err) {
      const message = (err as Error).message || '';
      if (message.includes('5 minut') || message.includes('minutes') || message.includes('cooldown')) {
        setResendStatus('cooldown');
      } else {
        setResendStatus('error');
      }
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return t('profile.noData');
    try {
      return new Date(dateStr).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('profile.title')}</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          {t('profile.subtitle')}
        </p>
      </div>

      {/* Profile Card */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-bold">
            {(user.displayName || user.email)[0].toUpperCase()}
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              {user.displayName || t('profile.defaultName')}
            </h2>
            <p className="text-slate-500 dark:text-slate-400">{user.email}</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Email */}
          <div className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('profile.email')}</p>
              <p className="text-slate-900 dark:text-white">{user.email}</p>
            </div>
            <div className="flex items-center gap-2">
              {user.emailVerified ? (
                <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {t('profile.verified')}
                </span>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1 text-sm text-amber-600 dark:text-amber-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {t('profile.unverified')}
                  </span>
                  <button
                    onClick={handleResendVerification}
                    disabled={resendStatus === 'sending' || resendStatus === 'sent'}
                    className="px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-200 bg-amber-100 dark:bg-amber-800/50 hover:bg-amber-200 dark:hover:bg-amber-800 border border-amber-300 dark:border-amber-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendStatus === 'sending' ? '...' :
                     resendStatus === 'sent' ? t('auth.verifyEmail.resendSuccess') :
                     resendStatus === 'cooldown' ? t('auth.verifyEmail.resendCooldown') :
                     resendStatus === 'error' ? t('auth.verifyEmail.resendError') :
                     t('auth.verifyEmail.resendButton')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Subscription Tier */}
          <div className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('profile.plan')}</p>
              <p className="text-slate-900 dark:text-white">
                {user.tier === 'pro' ? t('profile.proPlan') : user.tier === 'max' ? t('profile.maxPlan') : t('profile.freePlan')}
              </p>
            </div>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
              user.tier === 'pro'
                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
            }`}>
              {user.tier === 'pro' ? 'PRO' : user.tier === 'max' ? 'MAX' : 'FREE'}
            </span>
          </div>

          {/* Account Created */}
          <div className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('profile.accountCreated')}</p>
              <p className="text-slate-900 dark:text-white">{formatDate(user.createdAt)}</p>
            </div>
          </div>

          {/* User ID */}
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('profile.userId')}</p>
              <p className="text-slate-900 dark:text-white font-mono text-sm">
                {user.id.substring(0, 8)}...
              </p>
            </div>
            <button
              onClick={handleCopyId}
              className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              {isCopied ? t('profile.copied') : t('profile.copy')}
            </button>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          {t('profile.quickLinks')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to="/usage"
            className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <div>
              <p className="font-medium text-slate-900 dark:text-white">{t('profile.usageLink')}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('profile.usageLinkDesc')}</p>
            </div>
          </Link>

          <Link
            to="/bookmarks"
            className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
            <div>
              <p className="font-medium text-slate-900 dark:text-white">{t('profile.bookmarksLink')}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('profile.bookmarksLinkDesc')}</p>
            </div>
          </Link>

          {user.tier === 'free' && (
            <Link
              to="/pricing"
              className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors sm:col-span-2"
            >
              <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              <div>
                <p className="font-medium text-amber-900 dark:text-amber-100">{t('profile.upgradeToPro')}</p>
                <p className="text-sm text-amber-700 dark:text-amber-300">{t('profile.upgradeToProDesc')}</p>
              </div>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
